import {
  API_URL,
  v,
  put,
  debug,
  request,
  takeEvery,
  createDoRequestAction,
  assertTotalSchema,
  SagaIterator,
  call,
} from '../../lib'
import {
  CurrentMfaProof,
  freshAuthenticationProofBody,
  freshAuthenticationProofSchemaFragment,
  toProofSafeSchemaError,
} from '../../model/auth_proof'

import {doREQUEST_ERROR, doREQUEST_COMPLETE, doPROFILE_EMAIL_UPDATE} from '../state'
import {getToken} from './token_fetcher'

export const EMAIL_UPDATE_SUBMIT = 'EMAIL_UPDATE_SUBMIT'

const URL = '/v1/web/identity-email-update-submit'

export interface EmailUpdateSubmitRequestPayload {
  // A digit string, not a quantity: the backend generates ten digits from `randomInt(10)` and
  // compares them as a string, so roughly one code in ten begins with a zero that `Number()`
  // would eat. Same reasoning as the `verificationCode` inside a `currentMfaProof`, which is
  // already a string; this one was declared a number and validated with `v.number()` while the
  // wire carried the string all along, because `Joi.assert` converts a copy it then discards.
  verificationCode: string
  email: string
  currentPassword?: string
  currentMfaProof?: CurrentMfaProof
}

export const doEMAIL_UPDATE_SUBMIT = createDoRequestAction<EmailUpdateSubmitRequestPayload>(EMAIL_UPDATE_SUBMIT)

export const emailUpdateSubmitActionCreators = {
  doEMAIL_UPDATE_SUBMIT,
}

export function* emailUpdateSubmit({payload, setRequestResult}: any): SagaIterator {
  debug('saga*emailUpdateSubmit')

  try {
    assertTotalSchema({
      payload,
      schema: v.object({
        requestId: v.string().uuid().exist(),
        verificationCode: v.string().exist(),
        email: v
          .string()
          .email({tlds: {allow: false}})
          .exist(),
        ...freshAuthenticationProofSchemaFragment,
      }),
    })
  } catch (error) {
    // the allowlist, plus the error reduced to `isJoi` and a message. The ValidationError
    // itself carries the whole rejected payload three times over -- on `_original`, on
    // `details[].context.value` and, because `assertTotalSchema` goes through `Joi.assert`, in its
    // own `message` -- so it never reaches a REQUEST_ERROR. See `toProofSafeSchemaError`.
    return yield put(
      doREQUEST_ERROR(
        {
          fromType: EMAIL_UPDATE_SUBMIT,
          requestId: payload?.requestId,
          error: toProofSafeSchemaError(error),
        },
        setRequestResult,
      ),
    )
  }

  const {email, verificationCode} = payload

  const accessToken = yield call(getToken)

  const result = yield request({
    method: 'post',
    url: `${API_URL}${URL}`,
    accessToken,
    data: {
      verificationCode,
      email,
      ...freshAuthenticationProofBody(payload),
    },
  })

  if (!result.success || result.error) {
    // Never `debug(result.error)`: an axios error carries the request body on `config.data`, so
    // the raw error is itself a copy of the password. Only the two fields that say what
    // went wrong are logged.
    debug('saga*emailUpdateSubmit failed', {statusCode: result.statusCode, type: result.type})
    // no `fromPayload`, so `handle_expired_token` cannot replay this request. A replay
    // stripped of its proof would be applied as an unproved change that revokes every session.
    return yield put(
      doREQUEST_ERROR(
        {
          fromType: EMAIL_UPDATE_SUBMIT,
          requestId: payload.requestId,
          url: URL,
          statusCode: result.statusCode,
          type: result.type,
          // the 403's `payload.clientData.field` and `payload.errorHint` reach the form
          // through here, and the 410's `clientMessage` still does too
          payload: result.payload,
        },
        setRequestResult,
      ),
    )
  }

  yield put(doPROFILE_EMAIL_UPDATE({email}))

  yield put(
    doREQUEST_COMPLETE(
      {
        fromType: EMAIL_UPDATE_SUBMIT,
        requestId: payload.requestId,
        ...result,
      },
      setRequestResult,
    ),
  )
}

export function* emailUpdateSubmitSaga() {
  yield takeEvery(EMAIL_UPDATE_SUBMIT, emailUpdateSubmit)
}
